import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react';
import { routes } from '@portal/env/routes';
import { BlogPost } from '@portal/types/nexudus/blog';
import {
  documentToPlainTextString,
  readingTimeForArticle,
} from '@portal/env/utils/textHelpers';
import { ClockIcon } from '@portal/ui/bw-ui/checkout/icons';
import { Card } from '@common/bw-ui/ui/card';
import { useApp } from '../hooks/useApp';
import { Button } from '@common/bw-ui/ui/button';

const BlogPostCard: React.FC<{ blogPost: BlogPost }> = observer(
  ({ blogPost }) => {
    const { resourceImageBaseUrl } = useApp();
    const router = useRouter();

    if (!blogPost) return null;

    const readingTime = readingTimeForArticle(blogPost.FullText);
    const summaryTextPlain = documentToPlainTextString(blogPost.SummaryText);

    return (
      <Card className="tw:flex tw:flex-col tw:relative tw:w-full tw:h-full tw:border tw:border-gray-300 tw:overflow-hidden">
        <div
          className="tw:cursor-pointer"
          onClick={() => router.push(routes.post_by_id(blogPost))}
        >
          <div className="tw:relative tw:w-full tw:h-[200px] tw:rounded-t-lg tw:overflow-hidden">
            <img
              src={`${resourceImageBaseUrl}/blog/getlargeimage?id=${blogPost.Id}&w=800&h=400&mode=Crop&cache=${blogPost.UpdatedOn}`}
              alt={blogPost.Title}
              className="tw:w-full tw:h-full tw:object-cover"
            />
            {blogPost.BlogCategories.length > 0 && (
              <span className="tw:absolute tw:bottom-2 tw:right-4 tw:bg-white tw:text-black tw:px-2 tw:py-1 tw:rounded tw:text-xs tw:font-normal">
                {blogPost.BlogCategories[0]?.Title}
              </span>
            )}
          </div>

          <div className="tw:flex tw:flex-col tw:flex-1 tw:px-4 tw:mt-2">
            <div className="tw:flex tw:justify-between tw:items-start">
              <h5 className="tw:text-lg tw:font-medium tw:line-clamp-2">
                {blogPost.Title}
              </h5>
            </div>
            <p className="tw:prose tw:prose-slate tw:max-w-none tw:text-slate-900 tw:text-sm tw:font-light tw:line-clamp-2 tw:mb-4">
              {summaryTextPlain}
            </p>

            <div className="tw:flex tw:items-center tw:gap-2 tw:mt-auto tw:mb-4">
              <ClockIcon className="tw:w-4 tw:h-4 tw:text-gray-500" />
              <span className="tw:text-xs tw:text-gray-500 tw:font-light">
                {readingTime.minutes} min read
              </span>
            </div>
          </div>
        </div>
        <div
          className={`tw:w-full tw:p-2 tw:mt-auto tw:flex tw:flex-col tw:lg:flex-row tw:justify-between tw:bg-gray-200 tw:gap-2`}
        >
          <Button asChild size="lg" variant="gray" className="tw:w-full">
            <Link href={routes.post_by_id(blogPost)}>Read More</Link>
          </Button>
        </div>
      </Card>
    );
  },
);

export default BlogPostCard;
